import { create } from "zustand";
import { HttpStatusCode } from "axios";
import { TxDirectionEnum } from "@/config";
import {
  useUserStore,
  usePaymentStore,
  useAmlHistoryStore,
  useConfigStore,
} from "./";
import {
  amlbotAPI,
  type ExplorePostpaidAmlRequest,
  type ExplorePrepaidAmlRequest,
} from "@/api";

interface AmlCheckStore {
  externalData: any | null;
  hasAmlInputs: boolean;
  token: string | null;
  isLoading: boolean;
  errorCode: number | null;
  amlError: string | null;

  setExternalAmlData: (data: any) => void;
  clearExternalData: () => void;
  setToken: (token: string | null) => void;
  amlExplore: () => Promise<string | null>;
  explorePrepaid: () => Promise<string | null>;
  explorePostpaid: () => Promise<string | null>;
  clearAmlError: () => void;
}

export const useAmlCheckStore = create<AmlCheckStore>((set, get) => ({
  externalData: null,
  hasAmlInputs: false,
  token: null,
  isLoading: false,
  errorCode: null,
  amlError: null,

  clearAmlError: () => set({ errorCode: null, amlError: null }),
  setToken: (token: string | null) => set({ token }),

  setExternalAmlData: (data) => {
    if (data?.hash && data?.network) {
      set({
        externalData: {
          hash: data?.hash,
          network: data?.network,
          address: data?.address ?? null,
          direction: data?.direction ?? TxDirectionEnum.DEPOSIT,
        },
        hasAmlInputs: true,
      });
    } else {
      set({
        externalData: {
          hash: data?.hash || null,
          network: data?.network || null,
          address: data?.address || null,
          direction: data?.direction || TxDirectionEnum.DEPOSIT,
        },
        hasAmlInputs: false,
      });
    }
  },
  clearExternalData: () => {
    set({
      externalData: null,
      hasAmlInputs: false,
    });
  },
  amlExplore: async () => {
    const { token, explorePostpaid, explorePrepaid } = get();
    const { fetchUserData } = useUserStore.getState();
    const { discardPaymentIntentByToken } = usePaymentStore.getState();

    let result = null;

    try {
      const { isLoading } = get();

      if (isLoading) {
        return null;
      }

      set({ isLoading: true });
      set({ errorCode: null });

      if (token) {
        result = await explorePostpaid();
      } else {
        result = await explorePrepaid();
      }
    } catch (error: any) {
      const errorCodes = [
        HttpStatusCode.PaymentRequired,
        HttpStatusCode.UnprocessableEntity,
      ];

      if (errorCodes.includes(error?.status)) {
        const data = error.response.data;

        set({ errorCode: error?.status });
        set({ amlError: data?.message });
      }

      if (error?.status === HttpStatusCode.UnprocessableEntity && token) {
        discardPaymentIntentByToken(token);
      }

      console.error(error);
    } finally {
      set({ isLoading: false });
      fetchUserData();
    }

    return result;
  },
  explorePostpaid: async () => {
    const { clearExternalData, token } = get();
    const { setBalance } = useUserStore.getState();
    const { clearPaymentIntent } = usePaymentStore.getState();
    const { fetchAmlHistory } = useAmlHistoryStore.getState();
    let itemId = null;

    if (token) {
      const payload: ExplorePostpaidAmlRequest = {
        token: token,
      };

      const response = await amlbotAPI.explorePostpaid(payload);

      if (response.success) {
        const { data } = response.data;
        itemId = data?.uid;
        setBalance(response.data?.balance);
      }
    }

    await fetchAmlHistory();
    clearExternalData();
    clearPaymentIntent();

    return itemId;
  },
  explorePrepaid: async () => {
    const { clearExternalData, externalData } = get();
    const { appId, parentOrigin, externalId, username } =
      useConfigStore.getState();
    const { setBalance } = useUserStore.getState();
    const { clearPaymentIntent } = usePaymentStore.getState();
    const { fetchAmlHistory } = useAmlHistoryStore.getState();

    if (!appId || !parentOrigin || !externalId || !username || !externalData) {
      return null;
    }

    let amlItemId = null;

    const payload: ExplorePrepaidAmlRequest = {
      appId,
      parentOrigin,
      identifier: externalId,
      username,
      address: externalData?.address ?? "",
      network: externalData?.network ?? "",
      hash: externalData.hash,
      direction: externalData.direction,
    };
    const response = await amlbotAPI.explorePrepaid(payload);

    if (response.success) {
      const { data } = response.data;
      amlItemId = data?.uid;
      setBalance(response.data?.balance);
    }

    await fetchAmlHistory();
    clearExternalData();
    clearPaymentIntent();

    return amlItemId;
  },
}));
